import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import {
  getPsychometricTest,
  addTestQuestion,
  deleteTestQuestion,
  reorderTestQuestion,
  toggleTestStatus,
  type PsychometricTestDto,
  type TestQuestion,
} from '../../lib/psychometricTests';
import {
  ArrowLeft,
  Brain,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Power,
  HelpCircle,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const Route = createFileRoute('/kinerja/psychometrics/$testId/edit')({
  component: TestEditPage,
});

function TestEditPage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <TestEditContent />
    </ProtectedRoute>
  );
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  single_choice: 'Single Choice',
  multi_choice: 'Multiple Choice',
  scale: 'Scale',
  text: 'Open Text',
};

const QUESTION_TYPE_COLORS: Record<string, string> = {
  single_choice: 'bg-blue-100 text-blue-700',
  multi_choice: 'bg-purple-100 text-purple-700',
  scale: 'bg-emerald-100 text-emerald-700',
  text: 'bg-amber-100 text-amber-700',
};

type OptionDraft = { text: string; value: string };

function TestEditContent() {
  const { testId } = Route.useParams();

  const [test, setTest] = useState<PsychometricTestDto | null>(null);
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Status toggle
  const [statusLoading, setStatusLoading] = useState(false);

  // Add question form
  const [addingQ, setAddingQ] = useState(false);
  const [newQ, setNewQ] = useState({
    text: '',
    type: 'single_choice' as TestQuestion['type'],
    weight: '1',
  });
  const [options, setOptions] = useState<OptionDraft[]>([
    { text: '', value: '1' },
    { text: '', value: '0' },
  ]);
  const [qSaving, setQSaving] = useState(false);
  const [qError, setQError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPsychometricTest(testId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) {
        setTest(res.data.test);
        setQuestions(
          [...(res.data.test.questions ?? [])].sort(
            (a, b) => (a.orderIndex ?? a.order ?? 0) - (b.orderIndex ?? b.order ?? 0),
          ),
        );
      } else {
        setError(res.error ?? 'Failed to load test');
      }
    });
    return () => { cancelled = true; };
  }, [testId]);

  async function handleToggleStatus() {
    if (!test) return;
    setStatusLoading(true);
    const res = await toggleTestStatus(testId, !test.isActive);
    setStatusLoading(false);
    if (res.success && res.data) {
      toast.success(test.isActive ? 'Test deactivated' : 'Test activated');
      setTest(res.data.test);
    } else {
      toast.error(res.error ?? 'Failed to update test status');
    }
  }

  async function handleAddQuestion() {
    if (!newQ.text.trim()) return;
    setQSaving(true);
    setQError('');

    const needsOptions = newQ.type === 'single_choice' || newQ.type === 'multi_choice' || newQ.type === 'scale';
    const parsedOptions = needsOptions
      ? options
          .filter((o) => o.text.trim())
          .map((o) => ({ text: o.text.trim(), value: Number(o.value) || 0 }))
      : undefined;

    const res = await addTestQuestion(
      testId,
      {
        text: newQ.text.trim(),
        type: newQ.type,
        weight: Number(newQ.weight) || 1,
        options: parsedOptions,
      },
    );
    setQSaving(false);

    if (res.success && res.data) {
      toast.success('Question added');
      setQuestions((prev) => [...prev, res.data!.question]);
      setNewQ({ text: '', type: 'single_choice', weight: '1' });
      setOptions([{ text: '', value: '1' }, { text: '', value: '0' }]);
      setAddingQ(false);
    } else {
      toast.error(res.error ?? 'Failed to add question');
      setQError(res.error ?? 'Failed to add question');
    }
  }

  async function handleDeleteQuestion(q: TestQuestion) {
    if (!q.id) return;
    const res = await deleteTestQuestion(testId, q.id);
    if (res.success) {
      toast.success('Question deleted');
      setQuestions((prev) => prev.filter((x) => x.id !== q.id));
    } else {
      toast.error(res.error ?? 'Failed to delete question');
    }
  }

  async function handleMoveQuestion(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= questions.length) return;

    const updated = [...questions];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    const reordered = updated.map((q, i) => ({ ...q, orderIndex: i, order: i }));
    setQuestions(reordered);

    // Persist reorder for both questions
    const qA = reordered[idx];
    const qB = reordered[target];
    if (qA.id) await reorderTestQuestion(testId, qA.id, idx);
    if (qB.id) await reorderTestQuestion(testId, qB.id, target);
  }

  function addOptionRow() {
    setOptions((prev) => [...prev, { text: '', value: '0' }]);
  }

  function removeOptionRow(i: number) {
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="h-8 w-64 bg-muted rounded animate-pulse" />
        <div className="h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error || !test) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-muted-foreground text-sm">{error || 'Test not found'}</p>
        <Link to={'/kinerja/psychometrics' as any}>
          <Button variant="link" className="mt-2">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Tests
          </Button>
        </Link>
      </div>
    );
  }

  const needsOptions = newQ.type === 'single_choice' || newQ.type === 'multi_choice' || newQ.type === 'scale';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back */}
      <Link to={'/kinerja/psychometrics' as any}>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Tests
        </Button>
      </Link>

      {/* Test header */}
      <Card>
        <CardContent className="py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center shrink-0">
                <Brain className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-foreground">{test.name}</h1>
                {test.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{test.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-foreground font-medium">
                    {test.type}
                  </span>
                  {test.estimatedTime && (
                    <span className="text-xs text-muted-foreground">{test.estimatedTime} min</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {questions.length} question{questions.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Badge variant={test.isActive ? 'success' : 'secondary'}>
                {test.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={handleToggleStatus}
                disabled={statusLoading}
              >
                <Power className="w-3 h-3" />
                {statusLoading ? 'Saving…' : test.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question list */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-primary" />
              Questions ({questions.length})
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddingQ(true)}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Question
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-2">
          {questions.length === 0 && !addingQ ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No questions yet. Add the first question to build this test.
            </div>
          ) : (
            <div className="space-y-2">
              {questions.map((q, idx) => {
                const typeColor = QUESTION_TYPE_COLORS[q.type] ?? 'bg-gray-100 text-gray-600';
                return (
                  <div
                    key={q.id ?? idx}
                    className="flex items-start gap-3 bg-muted/50 rounded-lg px-3 py-2.5 group"
                  >
                    <span className="text-xs font-mono text-muted-foreground w-5 text-center shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-snug">{q.text}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={cn('text-xs px-1.5 py-0.5 rounded-full font-medium', typeColor)}>
                          {QUESTION_TYPE_LABELS[q.type] ?? q.type}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          w={q.options?.[0] !== undefined ? (q as any).weight ?? 1 : 1}
                        </span>
                        {q.options && q.options.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {q.options.length} option{q.options.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        onClick={() => handleMoveQuestion(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 rounded"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleMoveQuestion(idx, 1)}
                        disabled={idx === questions.length - 1}
                        className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 rounded"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(q)}
                        className="p-1 text-muted-foreground hover:text-destructive rounded"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add question form */}
          {addingQ && (
            <div className="mt-2 p-4 border rounded-lg bg-accent/40 space-y-4">
              <p className="text-sm font-medium text-foreground">New Question</p>

              <div className="space-y-1.5">
                <Label>Question text</Label>
                <Input
                  autoFocus
                  placeholder="e.g. How well do you handle stressful situations?"
                  value={newQ.text}
                  onChange={(e) => setNewQ((f) => ({ ...f, text: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select
                    value={newQ.type}
                    onValueChange={(v) => {
                      setNewQ((f) => ({ ...f, type: v as TestQuestion['type'] }));
                      if (v === 'text') setOptions([]);
                      else if (options.length === 0) setOptions([{ text: '', value: '1' }, { text: '', value: '0' }]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(QUESTION_TYPE_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Weight</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={newQ.weight}
                    onChange={(e) => setNewQ((f) => ({ ...f, weight: e.target.value }))}
                  />
                </div>
              </div>

              {/* Options */}
              {needsOptions && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Options</Label>
                    <button
                      onClick={addOptionRow}
                      className="text-xs text-primary hover:underline flex items-center gap-0.5"
                    >
                      <Plus className="w-3 h-3" />
                      Add option
                    </button>
                  </div>
                  {options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        className="flex-1 h-8 text-xs"
                        placeholder={`Option ${i + 1}`}
                        value={opt.text}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, idx) => (idx === i ? { ...o, text: e.target.value } : o)),
                          )
                        }
                      />
                      <Input
                        className="w-16 h-8 text-xs text-center"
                        type="number"
                        placeholder="Score"
                        value={opt.value}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, idx) => (idx === i ? { ...o, value: e.target.value } : o)),
                          )
                        }
                      />
                      <button
                        onClick={() => removeOptionRow(i)}
                        className="text-muted-foreground hover:text-destructive p-1 rounded shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">Score column: numeric value assigned when this option is selected.</p>
                </div>
              )}

              {qError && <p className="text-xs text-destructive">{qError}</p>}

              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddQuestion} className="flex-1" disabled={qSaving}>
                  {qSaving ? 'Saving…' : 'Add Question'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setAddingQ(false); setQError(''); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
